import { useEffect, useRef, useState } from "react";
import { register, call, remove } from "../funcStore";
import Header from "../components/layout/Header";
import MainTabs from "../components/layout/MainTabs";
import Footer from "../components/layout/Footer";
import { logAction } from "../api/logApi";
import { getPendingAttributes, approveAttribute } from "../api/adminApi";
import { rejectAttribute } from "../api/commonApi";
import { ATTRIBUTE_LABELS } from "../data/mockVtubers";
import { FILTER_GROUPS, FILTER_REV } from "../data/filterGroups";

function getAttributeOptions(attribute) {
  const rev = FILTER_REV[attribute];
  const group = rev && FILTER_GROUPS.find((g) => g.key === rev.groupKey);
  const filter = group && group.filters.find((f) => f.key === rev.key);

  if (!filter) {
    return [{ value: attribute, label: ATTRIBUTE_LABELS[attribute] ?? attribute }];
  }

  return filter.options;
}

const COMPONENT_KEY = "adminApprovePage";
const PAGE_SIZE = 50;

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "24px",
    maxWidth: "720px",
    margin: "0 auto",
    width: "80%",
  },
  title: {
    margin: 0,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "16px",
    background: "#fff",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
  },
  cardIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },
  cardName: {
    margin: 0,
    fontSize: "1rem",
  },
  recentThumbs: {
    display: "flex",
    flexWrap: "wrap",
  },
  recentThumb: {
    width: "300px",
    borderRadius: "4px",
    flexShrink: 0,
  },
  candidateList: {
    listStyle: "none",
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    padding: 0,
    margin: 0,
  },
  candidateItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    border: "1px dashed #999",
    borderRadius: "12px",
    padding: "2px 10px",
    fontSize: "0.85rem",
  },
  approveButton: {
    border: "none",
    background: "#4b3f8f",
    color: "#fff",
    borderRadius: "10px",
    padding: "2px 8px",
    fontSize: "0.75rem",
    cursor: "pointer",
  },
  rejectButton: {
    border: "1px solid #a33",
    background: "#fff",
    color: "#a33",
    borderRadius: "10px",
    padding: "2px 8px",
    fontSize: "0.75rem",
    cursor: "pointer",
  },
  attributeSelect: {
    fontSize: "0.8rem",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginTop: "8px",
  },
  pageButton: {
    padding: "6px 14px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    background: "#fff",
    cursor: "pointer",
  },
  pageButtonDisabled: {
    opacity: 0.5,
    cursor: "default",
  },
};

function AdminApprovePage() {
  // state
  const STATE_BEFORE_INIT = "beforeInit";
  const STATE_LOADING = "loading";
  const STATE_LOADED = "loaded";
  const STATE_NO_RESULTS = "noResults";
  const STATE_REFRESHING = "refreshing";

  const [state, setState] = useState(STATE_BEFORE_INIT);

  // data
  const dataRef = useRef({
    vtubers: [],
    total: 0,
    page: 1,
  });

  const selectRefsRef = useRef(new Map());

  async function load() {
    setState(STATE_LOADING);

    const [targetVtubers, total] = await getPendingAttributes(
      dataRef.current.page,
    );

    dataRef.current.vtubers = targetVtubers;
    dataRef.current.total = total;

    setState(
      dataRef.current.vtubers.length > 0 ? STATE_LOADED : STATE_NO_RESULTS,
    );
  }

  function removeCandidateAttribute(vtuberId, attribute) {
    dataRef.current.vtubers = dataRef.current.vtubers
      .map((vtuber) => {
        if (vtuber.id !== vtuberId) {
          return vtuber;
        }

        return {
          ...vtuber,
          candidate_attributes: vtuber.candidate_attributes.filter(
            (value) => value !== attribute,
          ),
        };
      })
      .filter((vtuber) => vtuber.candidate_attributes.length > 0);
  }

  const init = () => {
    register(`${COMPONENT_KEY}.getTotalPage`, () =>
      Math.max(1, Math.ceil(dataRef.current.total / PAGE_SIZE)),
    );

    register(`${COMPONENT_KEY}.nextPage`, () => {
      dataRef.current.page += 1;
      load();
    });

    register(`${COMPONENT_KEY}.prevPage`, () => {
      dataRef.current.page -= 1;
      load();
    });

    register(`${COMPONENT_KEY}.approve`, async (vtuberId, attribute, editedValue) => {
      const approvedValue = editedValue || attribute;

      logAction("attribute_approve", { vtuberId, attribute, approvedValue });

      if (approvedValue !== attribute) {
        await rejectAttribute(vtuberId, attribute);
      }
      await approveAttribute(vtuberId, approvedValue);

      removeCandidateAttribute(vtuberId, attribute);
      setState(STATE_REFRESHING);
    });

    register(`${COMPONENT_KEY}.reject`, async (vtuberId, attribute) => {
      logAction("attribute_reject", { vtuberId, attribute });
      await rejectAttribute(vtuberId, attribute);
      removeCandidateAttribute(vtuberId, attribute);
      setState(STATE_REFRESHING);
    });

    load();
  };

  // effect
  useEffect(() => {
    if (state === STATE_BEFORE_INIT) {
      init();
    }

    if (state === STATE_REFRESHING) {
      setState(
        dataRef.current.vtubers.length > 0 ? STATE_LOADED : STATE_NO_RESULTS,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    return () => {
      remove(`${COMPONENT_KEY}.getTotalPage`);
      remove(`${COMPONENT_KEY}.nextPage`);
      remove(`${COMPONENT_KEY}.prevPage`);
      remove(`${COMPONENT_KEY}.approve`);
      remove(`${COMPONENT_KEY}.reject`);
    };
  }, []);

  if (state === STATE_BEFORE_INIT) {
    return null;
  }

  const handleApprove = (vtuberId, attribute) => {
    const selectKey = `${vtuberId}:${attribute}`;
    const selectNode = selectRefsRef.current.get(selectKey);
    const editedValue = selectNode ? selectNode.value : attribute;

    call(`${COMPONENT_KEY}.approve`, vtuberId, attribute, editedValue);
  };

  const handleReject = (vtuberId, attribute) => {
    call(`${COMPONENT_KEY}.reject`, vtuberId, attribute);
  };

  const handleNavigateToDetail = (vtuberId) => {
    logAction("navigate_detail", { vtuberId });
    call("app.navigateToDetail", vtuberId);
  };

  return (
    <div style={styles.page}>
      <Header />

      <MainTabs />

      <main style={styles.main}>
        <h2 style={styles.title}>属性承認</h2>

        {state === STATE_LOADING && <p>読み込み中...</p>}

        {state === STATE_NO_RESULTS && (
          <p>承認待ちの属性はありません。</p>
        )}

        {(state === STATE_LOADED || state === STATE_REFRESHING) &&
          dataRef.current.vtubers.map((vtuber) => (
            <div key={vtuber.id} style={styles.card}>
              <div
                style={styles.cardHeader}
                onClick={() => handleNavigateToDetail(vtuber.id)}
              >
                <img
                  style={styles.cardIcon}
                  src={vtuber.icon_url}
                  alt={vtuber.name}
                />
                <h3 style={styles.cardName}>{vtuber.name}</h3>
              </div>

              {(vtuber.recent_thumbnail_urls ?? []).length > 0 && (
                <div style={styles.recentThumbs}>
                  {vtuber.recent_thumbnail_urls.map((thumbnailUrl, index) => (
                    <img
                      key={`${thumbnailUrl}-${index}`}
                      style={styles.recentThumb}
                      src={thumbnailUrl.replace("hqdefault", "mqdefault")}
                      alt={`${vtuber.name}の直近配信サムネ`}
                    />
                  ))}
                </div>
              )}

              <ul style={styles.candidateList}>
                {vtuber.candidate_attributes.map((attribute) => {
                  const selectKey = `${vtuber.id}:${attribute}`;

                  return (
                    <li key={attribute} style={styles.candidateItem}>
                      <select
                        style={styles.attributeSelect}
                        defaultValue={attribute}
                        ref={(el) => {
                          if (el) {
                            selectRefsRef.current.set(selectKey, el);
                          } else {
                            selectRefsRef.current.delete(selectKey);
                          }
                        }}
                      >
                        {getAttributeOptions(attribute).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        style={styles.approveButton}
                        onClick={() => handleApprove(vtuber.id, attribute)}
                      >
                        承認
                      </button>

                      <button
                        type="button"
                        style={styles.rejectButton}
                        onClick={() => handleReject(vtuber.id, attribute)}
                      >
                        却下
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

        {state !== STATE_LOADING &&
          call(`${COMPONENT_KEY}.getTotalPage`) > 1 && (
            <div style={styles.pagination}>
              <button
                type="button"
                style={{
                  ...styles.pageButton,
                  ...(dataRef.current.page === 1
                    ? styles.pageButtonDisabled
                    : {}),
                }}
                onClick={() => call(`${COMPONENT_KEY}.prevPage`)}
                disabled={dataRef.current.page === 1}
              >
                前へ
              </button>

              <span>
                {dataRef.current.page} /{" "}
                {call(`${COMPONENT_KEY}.getTotalPage`)}
              </span>

              <button
                type="button"
                style={{
                  ...styles.pageButton,
                  ...(dataRef.current.page ===
                  call(`${COMPONENT_KEY}.getTotalPage`)
                    ? styles.pageButtonDisabled
                    : {}),
                }}
                onClick={() => call(`${COMPONENT_KEY}.nextPage`)}
                disabled={
                  dataRef.current.page === call(`${COMPONENT_KEY}.getTotalPage`)
                }
              >
                次へ
              </button>
            </div>
          )}
      </main>

      <Footer />
    </div>
  );
}

export default AdminApprovePage;
